import Urn from '@adc/structures/urn'
import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react'
import {
  CopyButton,
  PageLayout,
  ResizablePanelGroup,
  Text,
  Icon,
  Button,
  SelectField,
  AiChatContainer,
  Animate,
  Modal,
  Loader,
  useApplicationNotifications,
  coreConstants,
  AutomationTarget,
} from '@adc/ui-morpheus'
import { NOTIFICATION_ROLES, TextAreaField } from '@adc/ui-core'
import * as configment from '@adc/configment'
import classnames from 'classnames/bind'
import * as graph from '@adc/sdk'
import * as blueprint from '@adc/blueprint'
import { connect } from 'react-redux'
import logger from '@adc/logger'
import ms from 'ms'
import { format } from 'date-fns'
import type {
  CognitionEvaluationTaskName,
  User,
  CognitionEvaluationGroup,
  CognitionEvaluationInputMessage,
} from '@adc/sdk/types'
import { seekTypes } from '@adc/seek'
import {
  fetchAiEvaluations,
  updateEvaluationGroup,
  fetchAllowedCognitionTasks,
  type CognitionTaskMetadata,
} from '../../providers/aiEvaluations'
import { ROOT_CRUMB } from '../../constants/routing'
import ContextSelector, { type ContextOutput } from '../contextSelector'
import { parseContext } from '../contextSelector/utils'
import StatusPill from '../../containers/aiEvaluationsPage/statusPill'
import ChatNotebookIcon from './chatNotebookIcon'
import WorkflowStateSection from './workflowStateSection'
import { TimeGroup, getTimeGroup } from './utils'
import styles from './styles.scss'

const cx = classnames.bind(styles)
const { ColorNames } = coreConstants

const PAGE_SIZE = 50
const MESSAGE_CHAR_LIMIT = 60
const HISTORY_REFRESH_DELAY_MS = ms('1s')

const chatHistoryFieldsConfig = {
  fragmentDefinitions: {
    ChatHistoryInputs: {
      fragment: graph.gql`
        fragment ChatHistoryInputs on CognitionEvaluationGroup {
          inputs(retrievalPolicy: ${graph.enums.CognitionEvaluationMessageRetrievalPolicy.Complete}) {
            ... on CognitionEvaluationChatInputMessage {
              sequence
              object {
                message
              }
            }
          }
        }
      `,
    },
    ChatHistoryOwner: {
      fragment: graph.gql`
        fragment ChatHistoryOwner on CognitionEvaluationGroup {
          owner {
            ... on Party {
              user {
                email
                display_name
              }
            }
          }
        }
      `,
    },
  },
}

type ChatMode = 'view' | 'new' | 'none'

/**
 * @param taskName
 * @param featureFlags
 * @returns true if related flag does not return false.
 *
 * Flag based permissions (for now) to allow for controlling access to particular tasks. If no flag defined, then true.
 *
 * BUYER_PDP_CHAT -> fe.resi.ai-assistant-buyer-pdp-chat
 * PROPERTY_PRICING_ASSISTANT -> fe.resi.ai-assistant-property-pricing-assistant
 * etc.
 */
const userCanUseTask = (
  taskName: string,
  featureFlags: Record<string, boolean> = {}
): boolean => {
  const flagName = `fe.resi.ai-assistant-${taskName
    .toLowerCase()
    .replace(/_/g, '-')}`
  return featureFlags[flagName] ?? true
}

interface ChatHistoryItemProps extends AutomationTarget {
  evalGroup: CognitionEvaluationGroup
  isSelected: boolean
  onClick: () => void
}

const ChatHistoryItem: React.FC<ChatHistoryItemProps> = ({
  evalGroup,
  isSelected,
  onClick,
  elmId = 'chat_history_item',
}) => {
  // Display name if available, otherwise show first input message
  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
  const displayText = useMemo(() => {
    if (evalGroup.name && evalGroup.name.trim()) {
      return evalGroup.name
    }

    // Find first input message (sequence=1)
    // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-assignment
    const firstInput = evalGroup.inputs?.find(
      // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-return
      (input: any) => input.sequence === 1
    )

    // @ts-ignore next
    // eslint-disable-next-line
    if (firstInput?.object?.message) {
      // @ts-ignore next
      // eslint-disable-next-line
      const message = firstInput.object.message as String
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-return
      return message.length > MESSAGE_CHAR_LIMIT
        ? `${message.substring(0, MESSAGE_CHAR_LIMIT)}...`
        : message
    }

    return 'Untitled Chat'
  }, [evalGroup])

  return (
    <div
      className={cx('chat-history-item', { selected: isSelected })}
      onClick={onClick}
      role='button'
      tabIndex={0}
      data-elm-id={elmId}
      onKeyDown={e => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          onClick()
        }
      }}
    >
      <Text size='small' weight={isSelected ? 'heavy' : 'normal'}>
        {displayText}
      </Text>
      {/* <Text size='tiny' color='gray'>
        {formattedDate}
      </Text> */}
    </div>
  )
}

interface ChatHistorySectionProps {
  selectedTask: string
  chatHistory: CognitionEvaluationGroup[]
  isLoadingHistory: boolean
  isLoadingMoreHistory: boolean
  selectedHistoryUrn: string | null
  onHistoryItemClick: (urn: string) => void
  onScroll: () => void
  listRef: React.RefObject<HTMLDivElement>
}

const ChatHistorySection: React.FC<ChatHistorySectionProps> = ({
  selectedTask,
  chatHistory,
  isLoadingHistory,
  isLoadingMoreHistory,
  selectedHistoryUrn,
  onHistoryItemClick,
  onScroll,
  listRef,
}) => {
  const groupedHistory = useMemo(() => {
    if (!chatHistory || chatHistory.length === 0) return []

    const referenceDate = new Date()
    const groups = new Map<TimeGroup, CognitionEvaluationGroup[]>()

    // Define group display order
    const groupOrder: TimeGroup[] = [
      'This Week',
      'Last Week',
      'This Month',
      'Last Month',
      'Older',
    ]

    // Initialize empty groups
    groupOrder.forEach(group => groups.set(group, []))

    // Assign each item to its group
    chatHistory.forEach(item => {
      if (!item.updated_date) return
      const group = getTimeGroup(item.updated_date as string, referenceDate)
      groups.get(group)?.push(item)
    })

    // Return ordered array, filtering empty groups
    return groupOrder
      .map(groupName => ({
        group: groupName,
        items: groups.get(groupName) || [],
      }))
      .filter(g => g.items.length > 0)
  }, [chatHistory])

  if (!selectedTask) {
    return null // Hide when no task selected
  }

  let content
  if (isLoadingHistory) {
    content = (
      <div className={cx('loading-container')}>
        <Loader size='small' />
        <Text size='tiny' color='gray'>
          Loading history...
        </Text>
      </div>
    )
  } else if (chatHistory.length === 0) {
    content = (
      <Text color='gray' as='p' size='tiny' elmId='no_chat_history_text'>
        No chat history yet. Start a new chat to begin.
      </Text>
    )
  } else {
    content = (
      <>
        <div
          className={cx('chat-history-list')}
          onScroll={onScroll}
          ref={listRef}
        >
          {groupedHistory.map(({ group, items }, groupIndex) => (
            <div key={group} className={cx('chat-history-group')}>
              <div className={cx('group-subheader')}>
                <Text size='tiny' weight='heavy' color='gray'>
                  {group}
                </Text>
              </div>
              {items.map((evalGroup, itemIndex) => (
                <ChatHistoryItem
                  key={evalGroup.urn}
                  evalGroup={evalGroup}
                  isSelected={selectedHistoryUrn === evalGroup.urn}
                  onClick={() => onHistoryItemClick(evalGroup.urn)}
                  elmId={`chat_history_item_${groupIndex}_${itemIndex}`}
                />
              ))}
            </div>
          ))}
        </div>
        {isLoadingMoreHistory && (
          <div className={cx('loading-more-container')}>
            <Icon
              name='arrow-rotate-right'
              className='fa-spin'
              iconStyle='regular'
              size='large'
              elmId='loading_more_history_icon'
            />
          </div>
        )}
      </>
    )
  }

  return (
    <div className={cx('section')}>
      <div className={cx('section-header')}>
        <Text.Header level={3} compact>
          Chat History
        </Text.Header>
      </div>
      {content}
    </div>
  )
}

interface ChatNotebookPageProps {
  user: User
  featureFlags: Record<string, boolean>
}

const ChatNotebookPage: React.FC<ChatNotebookPageProps> = ({
  user,
  featureFlags,
}) => {
  // Notifications hook
  const notifications = useApplicationNotifications()

  // Zero state: show full-page selector until a task is chosen (like AI Evals page)
  const [shouldShowZeroState, setShouldShowZeroState] = useState<boolean>(true)

  // Wide mode toggle (removes max-width for wide content like tables)
  const [isWideMode, setIsWideMode] = useState<boolean>(() => {
    try {
      return localStorage.getItem('chatNotebook.wideMode') === 'true'
    } catch {
      return false
    }
  })

  const toggleWideMode = useCallback(() => {
    setIsWideMode(prev => {
      const next = !prev
      try {
        localStorage.setItem('chatNotebook.wideMode', String(next))
      } catch {
        /* noop */
      }
      return next
    })
  }, [])

  // Chat mode state
  const [chatMode, setChatMode] = useState<ChatMode>('none')
  const chatModeRef = useRef<ChatMode>('none')
  useEffect(() => {
    chatModeRef.current = chatMode
  }, [chatMode])
  const [selectedTask, setSelectedTask] = useState<string>('')
  const [selectedContext, setSelectedContext] = useState<ContextOutput | null>(
    null
  )
  const [chatStarted, setChatStarted] = useState<boolean>(false)
  const [resetKey, setResetKey] = useState<number>(0)

  // Modal state for task switching confirmation
  const [showTaskSwitchModal, setShowTaskSwitchModal] = useState<boolean>(false)
  const [pendingTask, setPendingTask] = useState<string>('')

  // Chat history state
  const [chatHistory, setChatHistory] = useState<CognitionEvaluationGroup[]>([])
  const [isLoadingHistory, setIsLoadingHistory] = useState<boolean>(false)
  const [selectedHistoryUrn, setSelectedHistoryUrn] = useState<string | null>(
    null
  )
  const [historyOffset, setHistoryOffset] = useState<number>(0)
  const [hasMoreHistory, setHasMoreHistory] = useState<boolean>(true)
  const [isLoadingMoreHistory, setIsLoadingMoreHistory] =
    useState<boolean>(false)

  // Chat name editing state
  const [editableChatName, setEditableChatName] = useState<string>('')
  const [isChatInfoExpanded, setIsChatInfoExpanded] = useState<boolean>(false)
  const [isContextExpanded, setIsContextExpanded] = useState<boolean>(true)
  const [isRunAsExpanded, setIsRunAsExpanded] = useState<boolean>(true)
  const contextAutoCollapsedRef = useRef(false)

  // Auto-collapse context on first chat start, then leave it to the user
  useEffect(() => {
    if (chatStarted && !contextAutoCollapsedRef.current) {
      contextAutoCollapsedRef.current = true
      setIsContextExpanded(false)
    }
  }, [chatStarted])
  const [isSavingName, setIsSavingName] = useState<boolean>(false)
  const [firstMessageInNewChat, setFirstMessageInNewChat] = useState<string>('')
  const [isWaitingForNewChatUrn, setIsWaitingForNewChatUrn] =
    useState<boolean>(false)

  // Delete confirmation modal state
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false)
  const [isDeletingChat, setIsDeletingChat] = useState<boolean>(false)

  // Track current eval group status for edit/delete permissions
  const [currentEvalGroupStatus, setCurrentEvalGroupStatus] = useState<
    string | null
  >(null)

  // Track selected evaluation group
  const [selectedEvalGroup, setSelectedEvalGroup] =
    useState<CognitionEvaluationGroup | null>(null)

  // Refs for scroll handling
  const historyListRef = useRef<HTMLDivElement>(null)
  const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const hasCapturedNewChatUrn = useRef<boolean>(false)
  const capturedUrnRef = useRef<string | null>(null)

  // Utility function to compute display name from eval group
  // Reuses the same logic as ChatHistoryItem component
  const getDisplayNameFromEvalGroup = useCallback(
    (evalGroup: CognitionEvaluationGroup | null): string => {
      if (!evalGroup) return 'Untitled Chat'

      if (evalGroup.name && evalGroup.name.trim()) {
        return evalGroup.name
      }

      // Find first input message (sequence=1)
      // eslint-disable-next-line
      const firstInput = evalGroup.inputs?.find(
        // eslint-disable-next-line
        (input: CognitionEvaluationInputMessage) => input.sequence === 1
      )

      // @ts-ignore next
      const msg = firstInput?.object?.message // eslint-disable-line
      if (msg) {
        const message = msg as string
        // Don't truncate in the edit field, show full message
        return message
      }

      return 'Untitled Chat'
    },
    []
  )

  // Computed value to determine if editing/deleting is allowed
  // Requires: 1) valid URN exists, 2) eval group status is not PENDING, 3) not waiting for new chat URN
  const canEditOrDelete = useMemo(() => {
    return (
      selectedHistoryUrn !== null &&
      currentEvalGroupStatus !==
        graph.enums.CognitionEvaluationGroupStatus.Pending &&
      !isWaitingForNewChatUrn
    )
  }, [selectedHistoryUrn, currentEvalGroupStatus, isWaitingForNewChatUrn])

  // Create GraphQL WebSocket for AiChat (client-side only)
  const graphWebsocket = useMemo(() => {
    if (typeof window === 'undefined') return null
    return new blueprint.primitives.GraphWebsocket()
  }, [])

  // Fields configuration for chat history query
  const chatHistoryFields = useMemo(
    () => [
      'urn',
      'name',
      'updated_date',
      'created_date',
      'view_status',
      'status',
      'context.sources',
      'fragment:ChatHistoryInputs',
      'fragment:ChatHistoryOwner',
    ],
    []
  )

  // Fetch chat history batch (initial or append)
  const fetchChatHistoryBatch = useCallback(
    async (offset: number, append: boolean = false) => {
      if (!selectedTask || !user?.user_id) {
        if (!append) {
          setChatHistory([])
          setHasMoreHistory(true)
        }
        return
      }

      if (append) {
        setIsLoadingMoreHistory(true)
      } else {
        setIsLoadingHistory(true)
      }

      try {
        const dsl = {
          and: {
            expressions: [
              {
                equals: {
                  fields: 'task',
                  values: [selectedTask],
                },
              },
              {
                equals: {
                  fields: 'created_by',
                  values: [user.user_id],
                },
              },
              {
                equals: {
                  fields: 'view_status',
                  values: [
                    graph.enums.CognitionEvaluationGroupViewStatus.Active,
                  ],
                },
              },
            ],
          },
        }

        const result = await fetchAiEvaluations({
          seekType: seekTypes.COGNITION_EVALUATION_GROUPS,
          operationName: 'fetchChatHistory',
          // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-assignment
          dsl: dsl as any,
          fields: chatHistoryFields,
          fieldsConfig: chatHistoryFieldsConfig,
          sortField: 'updated_date',
          sortDirection: 'DESC',
          paginationState: {
            pageSize: PAGE_SIZE,
            offset,
          },
        })

        const content = result?.data?.content || []
        const totalCount = result?.data?.total_count || 0

        if (append) {
          setChatHistory(prev => [
            ...prev,
            ...(content as CognitionEvaluationGroup[]),
          ])
        } else {
          setChatHistory(content as CognitionEvaluationGroup[])
        }

        const newHasMore = offset + content.length < totalCount
        setHasMoreHistory(newHasMore)
      } catch (error) {
        logger.error('Error fetching chat history', error)
        if (!append) {
          setChatHistory([])
        }
      } finally {
        if (append) {
          setIsLoadingMoreHistory(false)
        } else {
          setIsLoadingHistory(false)
        }
      }
    },
    [selectedTask, user, chatHistoryFields]
  )

  // Refresh chat history with a delay
  const refreshHistoryWithDelay = useCallback(() => {
    setTimeout(() => {
      setHistoryOffset(0)
      setHasMoreHistory(true)
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      fetchChatHistoryBatch(0, false)
    }, HISTORY_REFRESH_DELAY_MS)
  }, [fetchChatHistoryBatch])

  // Fetch chat history when task changes
  useEffect(() => {
    setHistoryOffset(0)
    setHasMoreHistory(true)
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    fetchChatHistoryBatch(0, false)
  }, [selectedTask, user, fetchChatHistoryBatch])

  // Scroll detection for infinite loading
  const handleHistoryScroll = useCallback(() => {
    const scrollContainer = historyListRef.current
    if (!scrollContainer) return

    if (isLoadingMoreHistory || !hasMoreHistory || isLoadingHistory) return

    const { scrollTop, scrollHeight, clientHeight } = scrollContainer
    const distanceFromBottom = scrollHeight - scrollTop - clientHeight

    const SCROLL_THRESHOLD = 200

    if (distanceFromBottom < SCROLL_THRESHOLD) {
      const nextOffset = historyOffset + PAGE_SIZE
      setHistoryOffset(nextOffset)
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      fetchChatHistoryBatch(nextOffset, true)
    }
  }, [
    historyOffset,
    hasMoreHistory,
    isLoadingMoreHistory,
    isLoadingHistory,
    fetchChatHistoryBatch,
  ])

  // Throttled scroll handler
  const throttledScrollHandler = useCallback(() => {
    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current)
    }

    scrollTimeoutRef.current = setTimeout(() => {
      handleHistoryScroll()
    }, 100)
  }, [handleHistoryScroll])

  // Cleanup effect for scroll timeout
  useEffect(() => {
    return () => {
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current)
      }
    }
  }, [])

  // Task metadata fetched from graph (context_resources_supported, etc.)
  const [taskMetadataMap, setTaskMetadataMap] = useState<
    Map<string, CognitionTaskMetadata>
  >(new Map())

  useEffect(() => {
    fetchAllowedCognitionTasks()
      .then(tasks => {
        const map = new Map<string, CognitionTaskMetadata>()
        tasks.forEach(t => map.set(t.task, t))
        setTaskMetadataMap(map)
      })
      .catch(error => {
        logger.error('Error fetching allowed cognition tasks', error)
      })
  }, [])

  // Task options (curated chat tasks from SDK, filtered by feature flags AND allowed tasks)
  const taskOptions = useMemo(() => {
    return graph.utils.cognition.chatTasks
      .filter(value => userCanUseTask(value, featureFlags))
      .filter(value => taskMetadataMap.size === 0 || taskMetadataMap.has(value))
      .map(value => ({
        text: value
          .replace(/_/g, ' ')
          .toLowerCase()
          .replace(/\b\w/g, (l: string) => l.toUpperCase()),
        value,
      }))
      .sort((a, b) => a.text.localeCompare(b.text))
  }, [featureFlags, taskMetadataMap])

  const handleChatHistoryClick = useCallback(
    (urn: string) => {
      // Find the evaluation group from chat history
      const evalGroup = chatHistory.find(eg => eg.urn === urn)

      // Extract and parse context
      // @ts-ignore - context field is fetched but not in type definition
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
      let parsedContext = evalGroup?.context
        ? parseContext(evalGroup.context)
        : null

      // Infer run_as from owner: if owner email differs from current user, it was run-as
      // @ts-ignore - owner field is fetched via fragment but not in type definition
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      const ownerEmail = evalGroup?.owner?.user?.email as string | undefined
      // @ts-ignore - owner field is fetched via fragment but not in type definition
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      const ownerDisplayName = evalGroup?.owner?.user?.display_name as
        | string
        | undefined
      if (ownerEmail && ownerEmail !== user?.email) {
        if (parsedContext) {
          parsedContext = {
            ...parsedContext,
            run_as: new Urn('email', ownerEmail).toString(),
            run_as_display_name: ownerDisplayName,
          }
        } else {
          parsedContext = {
            sources: [],
            sourceType: 'listing',
            run_as: new Urn('email', ownerEmail).toString(),
            run_as_display_name: ownerDisplayName,
          }
        }
      }

      // Set the editable name
      setEditableChatName(getDisplayNameFromEvalGroup(evalGroup || null))

      // Set the current eval group status for permissions
      // @ts-ignore - status field is fetched but not in type definition
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      setCurrentEvalGroupStatus((evalGroup?.status as string) || null)

      // Update state
      setSelectedEvalGroup(evalGroup || null)
      setSelectedHistoryUrn(urn)
      setSelectedContext(parsedContext)
      setChatMode('view')
      setChatStarted(true)
    },
    [chatHistory, getDisplayNameFromEvalGroup]
  )

  const handleNewChat = () => {
    // Reset form state (keep selected task)
    setSelectedContext(null)
    setChatStarted(false)
    setResetKey(prev => prev + 1)
    setSelectedHistoryUrn(null)
    setSelectedEvalGroup(null)
    setEditableChatName('')
    setIsContextExpanded(true)
    setFirstMessageInNewChat('')
    setIsWaitingForNewChatUrn(false)
    setCurrentEvalGroupStatus(null)
    hasCapturedNewChatUrn.current = false
    capturedUrnRef.current = null

    // Set mode to 'new'
    setChatMode('new')
  }

  const handleSaveChatName = useCallback(async () => {
    if (!selectedHistoryUrn || isSavingName) return

    // For new chats, compare with first message; for view mode, compare with existing name
    let originalName: string
    if (chatMode === 'new') {
      originalName = firstMessageInNewChat
    } else {
      const currentEvalGroup = chatHistory.find(
        eg => eg.urn === selectedHistoryUrn
      )
      originalName = getDisplayNameFromEvalGroup(currentEvalGroup || null)
    }

    // If name hasn't changed, skip save
    if (editableChatName === originalName) return

    setIsSavingName(true)

    try {
      await updateEvaluationGroup({
        urn: selectedHistoryUrn,
        name: editableChatName.trim(),
        view_status: graph.enums.CognitionEvaluationGroupViewStatus.Active,
      })

      // Refresh chat history to show updated name
      refreshHistoryWithDelay()

      // Show success notification
      notifications.toast.open({ title: 'Chat name updated' })
      logger.info('Chat name updated')
    } catch (error) {
      logger.error('Error updating chat name', error)
      // Show error notification
      notifications.toast.open({
        title: 'Failed to update chat name',
        role: NOTIFICATION_ROLES.ERROR,
      })
      // Revert to original name on error
      setEditableChatName(originalName)
    } finally {
      setIsSavingName(false)
    }
  }, [
    selectedHistoryUrn,
    editableChatName,
    isSavingName,
    chatMode,
    firstMessageInNewChat,
    chatHistory,
    getDisplayNameFromEvalGroup,
    refreshHistoryWithDelay,
    notifications,
    canEditOrDelete,
    currentEvalGroupStatus,
    isWaitingForNewChatUrn,
  ])

  const handleDeleteChat = useCallback(async () => {
    if (!selectedHistoryUrn || isDeletingChat) return

    setIsDeletingChat(true)

    try {
      await updateEvaluationGroup({
        urn: selectedHistoryUrn,
        view_status: graph.enums.CognitionEvaluationGroupViewStatus.Inactive,
      })

      // Close modal
      setShowDeleteModal(false)

      // Reset to "none" mode
      setChatMode('none')
      setSelectedHistoryUrn(null)
      setSelectedEvalGroup(null)
      setSelectedContext(null)
      setChatStarted(false)
      setResetKey(prev => prev + 1)
      setEditableChatName('')
      setCurrentEvalGroupStatus(null)

      // Refresh chat history to remove deleted chat
      refreshHistoryWithDelay()

      // Show success notification
      notifications.toast.open({ title: 'Chat deleted successfully' })
      logger.info('Chat deleted successfully')
    } catch (error) {
      logger.error('Error deleting chat', error)
      // Show error notification
      notifications.toast.open({
        title: 'Failed to delete chat',
        role: NOTIFICATION_ROLES.ERROR,
      })
      setShowDeleteModal(false)
    } finally {
      setIsDeletingChat(false)
    }
  }, [
    selectedHistoryUrn,
    isDeletingChat,
    refreshHistoryWithDelay,
    notifications,
  ])

  // Transition out of zero state when task is selected
  useEffect(() => {
    if (selectedTask) {
      const timer = setTimeout(() => {
        setShouldShowZeroState(false)
        // Auto-start a new chat (skip the "Let's Go" intermediate state)
        setChatMode('new')
      }, ms('1s'))
      return () => clearTimeout(timer)
    } else {
      setShouldShowZeroState(true)
      return undefined
    }
  }, [selectedTask])

  const handleTaskChange = (_event: unknown, data: { value: string }) => {
    // If chat is in progress and task is changing, show confirmation modal
    if (chatStarted && selectedTask && data.value !== selectedTask) {
      setPendingTask(data.value)
      setShowTaskSwitchModal(true)
      return
    }

    setSelectedTask(data.value)
  }

  const handleConfirmTaskSwitch = () => {
    // Switch to the new task and reset chat
    setSelectedTask(pendingTask)
    setChatMode('none')
    setSelectedContext(null)
    setChatStarted(false)
    setResetKey(prev => prev + 1)
    setSelectedHistoryUrn(null)
    setSelectedEvalGroup(null)

    // Close modal
    setShowTaskSwitchModal(false)
    setPendingTask('')
  }

  const handleCancelTaskSwitch = () => {
    // Just close modal and keep current task
    setShowTaskSwitchModal(false)
    setPendingTask('')
  }

  const handleChatSend = (message: string, submitType: string) => {
    if (!chatStarted) {
      setChatStarted(true)
      // Capture first message for new chat name
      setFirstMessageInNewChat(message)
      // Use context address as chat name if available, else first message
      const defaultName = selectedContext?.contextTitle || message
      setEditableChatName(defaultName)
      // Explicitly clear any previous URN and status before waiting for new one
      setSelectedHistoryUrn(null)
      setCurrentEvalGroupStatus(null)
      setIsWaitingForNewChatUrn(true)
      hasCapturedNewChatUrn.current = false
      capturedUrnRef.current = null

      // Refresh chat history after a brief delay to allow backend to create the evaluation group
      refreshHistoryWithDelay()
    }
    logger.info('Chat message sent:', message, submitType)
  }

  const handleEvalGroupUpdate = useCallback(
    (evalGroup: CognitionEvaluationGroup | undefined) => {
      if (!evalGroup) return

      // For new chats, capture the URN when evaluation group is created
      if (
        chatModeRef.current === 'new' &&
        !hasCapturedNewChatUrn.current &&
        evalGroup.urn
      ) {
        setSelectedHistoryUrn(evalGroup.urn)
        setSelectedEvalGroup(evalGroup)
        setIsWaitingForNewChatUrn(false)
        hasCapturedNewChatUrn.current = true
        capturedUrnRef.current = evalGroup.urn

        // Auto-save context-derived name to the eval group
        if (selectedContext?.contextTitle) {
          updateEvaluationGroup({
            urn: evalGroup.urn,
            name: selectedContext.contextTitle.trim(),
            view_status: graph.enums.CognitionEvaluationGroupViewStatus.Active,
          }).catch(() => {})
        }
        return
      }

      // Only update eval group if it matches our current selection
      if (
        hasCapturedNewChatUrn.current &&
        evalGroup.urn !== capturedUrnRef.current
      ) {
        return
      }

      // Update the selected eval group with latest data
      setSelectedEvalGroup(evalGroup)

      // Update status in real-time
      // @ts-ignore - status field is fetched but not in type definition
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      const newStatus = (evalGroup.status as string) || null

      if (newStatus !== currentEvalGroupStatus) {
        setCurrentEvalGroupStatus(newStatus)
      }
    },
    [currentEvalGroupStatus, selectedContext]
  )

  // Memoized callback for Context selector — preserves existing run_as
  const handleContextChange = useCallback((ctx: ContextOutput | null) => {
    setSelectedContext(prev => {
      const existingRunAs = prev?.run_as
      const existingDisplayName = prev?.run_as_display_name
      if (ctx) {
        return existingRunAs
          ? {
              ...ctx,
              run_as: existingRunAs,
              run_as_display_name: existingDisplayName,
            }
          : ctx
      }
      // Context cleared but preserve run_as
      return existingRunAs
        ? {
            sources: [],
            sourceType: 'listing',
            run_as: existingRunAs,
            run_as_display_name: existingDisplayName,
          }
        : null
    })
  }, [])

  // Memoized callback for Run As selector — merges run_as into existing context
  const handleRunAsChange = useCallback((ctx: ContextOutput | null) => {
    const runAs = ctx?.run_as
    const runAsDisplayName = ctx?.run_as_display_name
    setSelectedContext(prev => {
      if (runAs) {
        return prev
          ? { ...prev, run_as: runAs, run_as_display_name: runAsDisplayName }
          : {
              sources: [],
              sourceType: 'listing',
              run_as: runAs,
              run_as_display_name: runAsDisplayName,
            }
      }
      // run_as cleared
      if (prev) {
        const {
          run_as: _removed,
          run_as_display_name: _removedName,
          ...rest
        } = prev
        return rest.sources?.length ? rest : null
      }
      return null
    })
  }, [])

  const transformContextForChat = (contextOutput: ContextOutput | null) => {
    if (!contextOutput?.sources || contextOutput.sources.length === 0)
      return undefined

    return contextOutput.sources.map(sourceUrn => {
      const listingId = Urn.safelyParse(sourceUrn)?.identifier

      return {
        urn: sourceUrn,
        url: `${configment.get(
          'MORPHEUS_HOST',
          'https://morpheus.aucn.io'
        )}/property/listings/${listingId}/view`,
        linkText: `Listing ${listingId}`,
      }
    })
  }

  // Compute context prop for AiChatContainer.
  // AiChatContainer requires context.length > 0 to include run_as in blueprintContext.
  // When we have run_as but no listing sources, pass a sourceless placeholder so
  // AiChatContainer includes run_as. The placeholder URN is filtered out server-side.
  const chatContext = useMemo(() => {
    const transformed = transformContextForChat(selectedContext)
    if (transformed && transformed.length > 0) return transformed
    // If no sources but run_as exists, pass a minimal entry to satisfy length > 0 check
    if (selectedContext?.run_as)
      return [{ urn: undefined, url: '', linkText: '' }]
    return undefined
  }, [selectedContext])

  return (
    <PageLayout
      title='Chat Notebook'
      crumbs={[ROOT_CRUMB, 'Chat Notebook']}
      buttons={[
        !selectedTask ? null : (
          <div
            key='task-selector-container'
            className={cx('task-selector-container')}
          >
            <Text
              size='tiny'
              color='gray'
              weight='heavy'
              style={{ whiteSpace: 'nowrap' }}
            >
              AI Assistant:
            </Text>
            <div className={cx('task-selector')}>
              <SelectField
                hideWrapper
                key='task-selector'
                value={selectedTask}
                onChange={handleTaskChange}
                options={taskOptions}
                placeholder='Select an AI Assistant'
                compact
                elmId='header_task_selector'
              />
            </div>
          </div>
        ),
      ]}
    >
      {shouldShowZeroState ? (
        <div className={cx('zero-state')}>
          <Animate.FadeInOut isVisible={!selectedTask} duration={500}>
            <div className={cx('zero-state-inner')}>
              <div className={cx('zero-state-brand')}>
                <ChatNotebookIcon />
                <span className={cx('zero-state-title')}>Chat Notebook</span>
              </div>
              <div className={cx('zero-state-selector')}>
                <SelectField
                  hideWrapper
                  key='zero-state-task-selector'
                  value={selectedTask}
                  onChange={handleTaskChange}
                  options={taskOptions}
                  placeholder='Select an AI Assistant'
                  compact
                  elmId='zero_state_task_selector'
                />
              </div>
            </div>
          </Animate.FadeInOut>
        </div>
      ) : (
        <ResizablePanelGroup
          elmId='chat_notebook'
          leftPanel={{ defaultSize: 25 }}
          rightPanel={{ defaultSize: 25 }}
          cls={{
            leftPanel: cx('side-panel'),
            mainPanel: cx(
              'center-panel',
              chatMode === 'new' && selectedTask ? 'new' : ''
            ),
            rightPanel: cx('side-panel'),
          }}
          slots={{
            left: () => (
              <>
                <div className={cx('section', 'bottom-divider')}>
                  <Button
                    elmId='new_chat_button'
                    style={{ marginBottom: '12px' }}
                    role='primary'
                    fluid
                    onClick={handleNewChat}
                    disabled={!selectedTask}
                  >
                    New Chat
                    <Icon
                      name='message-plus'
                      color='unset'
                      iconStyle='regular'
                      style={{ marginLeft: '9px', fontSize: '18px' }}
                      elmId='new_chat_icon'
                    />
                  </Button>
                </div>
                <ChatHistorySection
                  selectedTask={selectedTask}
                  chatHistory={chatHistory}
                  isLoadingHistory={isLoadingHistory}
                  isLoadingMoreHistory={isLoadingMoreHistory}
                  selectedHistoryUrn={selectedHistoryUrn}
                  onHistoryItemClick={handleChatHistoryClick}
                  onScroll={throttledScrollHandler}
                  listRef={historyListRef}
                />
              </>
            ),
            main: () => (
              <>
                {selectedTask &&
                  (chatMode === 'view' || chatMode === 'new') && (
                    <div className={cx('wide-toggle-bar')}>
                      <Button
                        elmId='wide_mode_toggle'
                        role='tertiary'
                        size='small'
                        collapse
                        active={isWideMode}
                        onClick={toggleWideMode}
                      >
                        <Icon
                          name='arrows-left-right-to-line'
                          iconStyle='regular'
                          elmId='wide_mode_icon'
                        />
                      </Button>
                    </div>
                  )}

                {selectedTask && chatMode === 'view' && selectedHistoryUrn && (
                  <div className={cx('chat-container', { wide: isWideMode })}>
                    <AiChatContainer
                      // @ts-ignore next
                      graphWebsocket={graphWebsocket}
                      taskName={selectedTask as CognitionEvaluationTaskName}
                      initialEvaluationGroupUrn={selectedHistoryUrn}
                      elmId='chat_notebook_page_chat_history'
                      open
                      context={chatContext}
                      runAs={selectedContext?.run_as}
                      onEvalGroupUpdate={handleEvalGroupUpdate}
                    />
                  </div>
                )}
                {selectedTask && chatMode === 'new' && (
                  <div className={cx('chat-container', { wide: isWideMode })}>
                    <AiChatContainer
                      // @ts-ignore next
                      graphWebsocket={graphWebsocket}
                      taskName={selectedTask as CognitionEvaluationTaskName}
                      context={chatContext}
                      runAs={selectedContext?.run_as}
                      elmId='chat_notebook_page_chat'
                      onSend={handleChatSend}
                      open
                      resetKey={resetKey}
                      onEvalGroupUpdate={handleEvalGroupUpdate}
                      showProgress
                      showPrivateMessage={false}
                    />
                  </div>
                )}
              </>
            ),
            right: () => (
              <>
                {(chatMode === 'new' || chatMode === 'view') &&
                  selectedTask && (
                    <>
                      {/* Chat Details: collapsible, closed by default */}
                      {chatStarted && (
                        <div
                          className={cx('collapsible-section')}
                          style={{ flex: '0 0 auto' }}
                        >
                          <div
                            className={cx('collapsible-header')}
                            onClick={() => setIsChatInfoExpanded(prev => !prev)}
                            role='button'
                            tabIndex={0}
                            onKeyDown={e => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault()
                                setIsChatInfoExpanded(prev => !prev)
                              }
                            }}
                            data-elm-id='chat_info_toggle'
                          >
                            <div className={cx('collapsible-header-left')}>
                              <Text.Header
                                level={3}
                                style={{ whiteSpace: 'nowrap' }}
                                compact
                              >
                                {chatMode === 'new'
                                  ? 'New Chat'
                                  : 'Chat Details'}
                              </Text.Header>
                              {selectedHistoryUrn && (
                                <CopyButton
                                  text={selectedHistoryUrn}
                                  size='small'
                                  elmId='copy_urn'
                                  showSuccessTextInButton
                                  successMessage='URN copied'
                                />
                              )}
                            </div>
                            {chatMode === 'view' && currentEvalGroupStatus && (
                              <StatusPill
                                status={
                                  currentEvalGroupStatus as graph.enums.CognitionEvaluationGroupStatus
                                }
                                size='x-small'
                                elmId='chat_status_pill'
                                style={{ alignSelf: 'center' }}
                              />
                            )}
                            <Icon
                              name={
                                isChatInfoExpanded
                                  ? 'chevron-down'
                                  : 'chevron-up'
                              }
                              iconStyle='solid'
                              size='small'
                              color='gray'
                              elmId='chat_info_chevron'
                            />
                          </div>
                          {isChatInfoExpanded && (
                            <div
                              className={cx(
                                'collapsible-content',
                                'ui',
                                'form'
                              )}
                            >
                              <div className={cx('section-header')}>
                                <Text weight='heavy'>Chat Name</Text>
                              </div>
                              <TextAreaField
                                style={{ lineHeight: '23px' }}
                                rows={2}
                                compact
                                inputLabel='CHAT NAME'
                                value={editableChatName}
                                onChange={(
                                  e: React.ChangeEvent<HTMLInputElement>
                                ) => setEditableChatName(e.target.value)}
                                onBlur={handleSaveChatName}
                                onKeyDown={(
                                  e: React.KeyboardEvent<HTMLTextAreaElement>
                                ) => {
                                  if (e.key === 'Enter') {
                                    e.preventDefault()
                                    // eslint-disable-next-line @typescript-eslint/no-floating-promises
                                    handleSaveChatName()
                                  }
                                }}
                                placeholder='Enter chat name...'
                                disabled={isSavingName || !canEditOrDelete}
                                fluid
                                elmId='chat_name_input'
                                maxLength={128}
                              />
                              <div className={cx('section-header')}>
                                <Text weight='heavy'>Updated On</Text>
                              </div>
                              {selectedEvalGroup?.updated_date ? (
                                <Text color='gray' style={{ margin: '12px' }}>
                                  {format(
                                    new Date(
                                      selectedEvalGroup.updated_date as string
                                    ),
                                    'EEEE MMM dd, yyyy, h:mm a'
                                  )}
                                </Text>
                              ) : null}
                              {/* Delete button */}
                              {chatMode === 'view' && selectedHistoryUrn && (
                                <div
                                  style={{
                                    marginTop: '12px',
                                    display: 'flex',
                                    justifyContent: 'flex-end',
                                  }}
                                >
                                  <Button
                                    role='tertiary'
                                    color={ColorNames.Error}
                                    size='small'
                                    onClick={() => setShowDeleteModal(true)}
                                    disabled={
                                      isDeletingChat || !canEditOrDelete
                                    }
                                    elmId='delete_chat_button'
                                  >
                                    <Icon
                                      name='trash'
                                      iconStyle='regular'
                                      elmId='delete_chat_icon'
                                      style={{ marginRight: '6px' }}
                                    />
                                    Delete Chat
                                  </Button>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                      {/* Context section: show for new chats if task supports any context resource;
                       after chat started or in view mode, show only if context exists */}
                      {(() => {
                        const taskMeta = taskMetadataMap.get(selectedTask)
                        const supportsContext =
                          (taskMeta?.context_resources_supported?.length ?? 0) >
                          0
                        // Use selectedEvalGroup.context (stable seek data) for view/started mode
                        // to avoid transient null from ContextSelector remount
                        const hasContext =
                          chatMode === 'view' || chatStarted
                            ? Boolean(
                                selectedEvalGroup?.context?.sources?.length ||
                                  selectedContext?.sources?.length
                              )
                            : Boolean(selectedContext?.sources?.length)

                        // After chat started (new or view): only show if context exists
                        if (chatStarted) return hasContext ? undefined : null
                        // New chat, before message sent: show if task supports context
                        if (chatMode === 'new')
                          return supportsContext ? undefined : null
                        // View mode: show if eval group has context
                        if (chatMode === 'view')
                          return selectedEvalGroup?.context ? undefined : null
                        return null
                      })() !== null && (
                        <div className={cx('collapsible-section')}>
                          <div
                            className={cx('collapsible-header')}
                            onClick={() => setIsContextExpanded(prev => !prev)}
                            role='button'
                            tabIndex={0}
                            onKeyDown={e => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault()
                                setIsContextExpanded(prev => !prev)
                              }
                            }}
                            data-elm-id='context_toggle'
                          >
                            <div className={cx('collapsible-header-left')}>
                              <Text.Header level={3} compact>
                                Context
                              </Text.Header>
                              {selectedContext?.contextTitle && (
                                <Text color='gray' size='small'>
                                  {selectedContext.contextTitle}
                                </Text>
                              )}
                            </div>
                            <Icon
                              name={
                                isContextExpanded
                                  ? 'chevron-down'
                                  : 'chevron-up'
                              }
                              iconStyle='solid'
                              size='small'
                              color='gray'
                              elmId='context_chevron'
                            />
                          </div>
                          <div
                            className={cx('ui form')}
                            style={
                              isContextExpanded
                                ? undefined
                                : { display: 'none' }
                            }
                          >
                            <ContextSelector
                              key={
                                chatMode === 'view' ? selectedHistoryUrn : 'new'
                              }
                              onContextChange={handleContextChange}
                              initialContext={selectedContext}
                              elmId='chat_context_selector'
                              viewMode={chatStarted}
                              enableRunAs={false}
                              hideLabel
                              hideRunAs
                              contextResourcesSupported={
                                taskMetadataMap.get(selectedTask)
                                  ?.context_resources_supported ?? ['listing']
                              }
                            />
                          </div>
                        </div>
                      )}
                      {/* Run As User section: show for all new chats (before message sent);
                       after chat started or in view mode, show only if run_as was specified */}
                      {(() => {
                        const hasRunAs = Boolean(selectedContext?.run_as)
                        const hasAimAuth =
                          user?.authorities?.includes('AIM') ?? false

                        // After chat started (new or view): only show if run_as exists
                        if (chatStarted) return hasRunAs
                        // New chat, before message sent: show if user has AIM
                        if (chatMode === 'new') return hasAimAuth
                        // View mode: show if run_as exists
                        return hasRunAs
                      })() && (
                        <div className={cx('collapsible-section')}>
                          <div
                            className={cx('collapsible-header')}
                            onClick={() => setIsRunAsExpanded(prev => !prev)}
                            role='button'
                            tabIndex={0}
                            onKeyDown={e => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault()
                                setIsRunAsExpanded(prev => !prev)
                              }
                            }}
                            data-elm-id='run_as_toggle'
                          >
                            <div className={cx('collapsible-header-left')}>
                              <Text.Header level={3} compact>
                                Run As User
                              </Text.Header>
                              {selectedContext?.run_as && (
                                <Text color='gray' size='small'>
                                  {selectedContext.run_as_display_name ||
                                    (selectedContext.run_as.startsWith(
                                      'adc:anonymous_session:'
                                    )
                                      ? 'Unauthenticated'
                                      : selectedContext.run_as
                                          .replace('adc:email:', '')
                                          .split('@')[0])}
                                </Text>
                              )}
                            </div>
                            <Icon
                              name={
                                isRunAsExpanded ? 'chevron-down' : 'chevron-up'
                              }
                              iconStyle='solid'
                              size='small'
                              color='gray'
                              elmId='run_as_chevron'
                            />
                          </div>
                          {isRunAsExpanded && (
                            <div className={cx('collapsible-content')}>
                              {chatStarted ? (
                                <Text color='gray' style={{ margin: '12px' }}>
                                  {(() => {
                                    if (!selectedContext?.run_as) return 'N/A'
                                    if (
                                      selectedContext.run_as.startsWith(
                                        'adc:anonymous_session:'
                                      )
                                    )
                                      return 'Unauthenticated User'
                                    return selectedContext.run_as.replace(
                                      'adc:email:',
                                      ''
                                    )
                                  })()}
                                </Text>
                              ) : (
                                <ContextSelector
                                  key={`runas_${selectedHistoryUrn || 'new'}`}
                                  onContextChange={handleRunAsChange}
                                  initialContext={selectedContext}
                                  elmId='chat_run_as_selector'
                                  enableRunAs={
                                    user?.authorities?.includes('AIM') ?? false
                                  }
                                  hideLabel
                                  hideContext
                                />
                              )}
                            </div>
                          )}
                        </div>
                      )}{' '}
                      {/* Workflow State: only for WORKFLOW_ADVANCEMENT tasks */}
                      {selectedTask === 'WORKFLOW_ADVANCEMENT' && (
                        <WorkflowStateSection
                          contextUrn={selectedContext?.sources?.[0] ?? null}
                          contextSources={selectedContext?.sources ?? []}
                        />
                      )}
                    </>
                  )}
              </>
            ),
          }}
        />
      )}

      <Modal
        open={showTaskSwitchModal}
        onClose={handleCancelTaskSwitch}
        size='tiny'
      >
        <Modal.Header>Chat in progress...</Modal.Header>
        <Modal.Content>
          Changing tasks will leave this chat. Are you sure?
        </Modal.Content>
        <Modal.Actions>
          <Button role='secondary' onClick={handleCancelTaskSwitch}>
            No, keep chatting
          </Button>
          <Button role='primary' onClick={handleConfirmTaskSwitch}>
            Yes, continue
          </Button>
        </Modal.Actions>
      </Modal>

      {/* Delete confirmation modal */}
      <Modal
        open={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        size='tiny'
        elmId='delete_chat_modal'
      >
        <Modal.Header>Delete Chat</Modal.Header>
        <Modal.Content>
          <Text style={{ textWrap: 'pretty' }}>
            Are you sure you want to delete this chat? This action cannot be
            undone.
          </Text>
        </Modal.Content>
        <Modal.Actions>
          <Button
            role='secondary'
            onClick={() => setShowDeleteModal(false)}
            disabled={isDeletingChat}
            elmId='delete_cancel_button'
          >
            Cancel
          </Button>
          <Button
            role='primary'
            onClick={handleDeleteChat}
            disabled={isDeletingChat}
            elmId='delete_confirm_button'
          >
            {isDeletingChat ? (
              <>
                <Icon
                  name='spinner'
                  className='fa-spin'
                  style={{ marginRight: '9px' }}
                  elmId='deleting_chat_spinner'
                />
                Deleting...
              </>
            ) : (
              'Delete'
            )}
          </Button>
        </Modal.Actions>
      </Modal>
    </PageLayout>
  )
}

const mapStateToProps = (store: {
  user: User
  featureFlags: Record<string, boolean>
}) => ({
  user: store.user,
  featureFlags: store.featureFlags,
})

export default connect(mapStateToProps)(ChatNotebookPage)
